import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { X, CheckCircle } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../../../lib/utils";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "../../../components/ui/dialog";

export function BatchOverrideModal({
  open,
  onOpenChange,
  productName,
  batches = [],
  initialAllocation = {}, // Map of { batchNumber: quantity }
  targetQty = 0,
  onConfirm,
}) {
  const { t } = useTranslation();
  const [allocations, setAllocations] = useState({});

  // Reset/load initial values when modal opens
  useEffect(() => {
    if (open) {
      const initial = {};
      batches.forEach((b) => {
        initial[b.batchNumber] = initialAllocation[b.batchNumber] || 0;
      });
      setAllocations(initial);
    }
  }, [open, batches, initialAllocation]);

  const handleQtyChange = (batchNumber, value, maxAvailable) => {
    const num = Math.max(0, Math.min(maxAvailable, Number(value) || 0));
    setAllocations((prev) => ({
      ...prev,
      [batchNumber]: num,
    }));
  };

  const totalAllocated = useMemo(() => {
    return Object.values(allocations).reduce((acc, qty) => acc + qty, 0);
  }, [allocations]);

  const handleSave = () => {
    // Filter out zero quantities and return the allocated map
    const result = {};
    Object.entries(allocations).forEach(([batchNumber, qty]) => {
      if (qty > 0) {
        result[batchNumber] = qty;
      }
    });
    onConfirm(result);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/40 backdrop-blur-sm fixed inset-0 z-40 transition-opacity animate-in fade-in duration-200" />

        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
            "w-[95vw] sm:w-[90vw] md:w-[600px]",
            "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60",
            "p-6 flex flex-col gap-5 font-sans focus:outline-none",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4.5 w-4.5 text-[var(--color-primary)]" />
              <DialogTitle className="text-[16px] font-bold text-foreground">
                {t("inventory.overrideAllocationBatches")}
              </DialogTitle>
            </div>
            <DialogPrimitive.Close className="h-8 w-8 grid place-items-center rounded-lg text-[var(--color-text-secondary)] hover:bg-[var(--color-workspace)]/60 hover:text-[var(--color-primary)] transition focus:outline-none">
              <X className="h-4 w-4" />
              <span className="sr-only">{t("formActions.close")}</span>
            </DialogPrimitive.Close>
          </div>

          <div className="space-y-1">
            <h4 className="text-xs font-bold text-foreground truncate">{productName}</h4>
            <div className="flex items-center justify-between text-[11px] text-[var(--color-text-secondary)] font-semibold">
              <span>{t("validation.targetRequiredQty")} <strong className="text-foreground font-mono">{targetQty}</strong></span>
              <span>{t("inventory.runningAllocatedTotal")} <strong className={cn("font-mono", totalAllocated === targetQty ? "text-emerald-600" : "text-[var(--color-primary)]")}>{totalAllocated}</strong></span>
            </div>
          </div>

          {/* Batches Table List */}
          <div className="border border-[var(--color-text-border)] rounded-xl overflow-hidden max-h-[300px] overflow-y-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-[var(--color-workspace)]/40 border-b border-[var(--color-text-border)] font-bold text-[10px] uppercase text-[var(--color-text-secondary)] tracking-wider">
                  <th className="px-4 py-2.5">{t("inventory.batchNumber")}</th>
                  <th className="px-4 py-2.5">{t("inventory.warehouse")}</th>
                  <th className="px-4 py-2.5 text-center">{t("status.available")}</th>
                  <th className="px-4 py-2.5">{t("inventory.warrantyEnd")}</th>
                  <th className="px-4 py-2.5 text-right w-[110px]">{t("inventory.allocatedQty")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 font-medium">
                {batches.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-gray-400 font-semibold">
                      {t("inventory.noAvailableBatches")}
                    </td>
                  </tr>
                ) : (
                  batches.map((b) => (
                    <tr key={b.batchNumber} className="hover:bg-[var(--color-workspace)]/10">
                      <td className="px-4 py-3 font-mono font-bold text-foreground">
                        {b.batchNumber}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {b.warehouse}
                      </td>
                      <td className="px-4 py-3 text-center font-mono text-foreground">
                        {b.availableQty}
                      </td>
                      <td className="px-4 py-3 text-gray-500 font-semibold text-[11px]">
                        {b.warrantyEndDate ? new Date(b.warrantyEndDate).toLocaleDateString("en-IN", { month: "short", year: "numeric" }) : t("inventory.noExpiry")}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <input
                          type="number"
                          min="0"
                          max={b.availableQty}
                          value={allocations[b.batchNumber] || ""}
                          onChange={(e) => handleQtyChange(b.batchNumber, e.target.value, b.availableQty)}
                          placeholder="0"
                          className="w-[85px] h-8 px-2 rounded-lg border border-[var(--color-text-border)] text-right font-mono text-xs font-bold focus:border-[var(--color-accent)] focus:outline-none"
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-[var(--color-text-border)]/50 shrink-0">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="h-10 px-4 rounded-xl border border-[var(--color-text-border)] text-[var(--color-text-secondary)] font-bold text-xs hover:bg-gray-50 transition cursor-pointer"
            >
              {t("formActions.cancel")}
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="h-10 px-5 rounded-xl bg-[var(--color-primary)] hover:bg-[var(--color-accent)] text-white font-bold text-xs transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] cursor-pointer"
            >
              {t("formActions.saveAllocation")}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
